import { useState } from "react";
import moment from "moment-timezone";
import { getAzureBlob } from "../../../services/container";
import { downloadFile } from "../../../shared/utils/utils";
import { formattedTypeContainer } from "../../../components/formatted/FormattedText";
import { startWithLowercase } from "../../../components/utils/Date";
import { buildCollectionFluxLookup } from "../utils/collectionExportFlux";
import { buildOutputSurfaceBlobUrl } from "../utils/outputSurfaceBlobUrl";
import { useSuperContainers } from "./useSuperContainers";
/**
 * Hook pour gérer l'export PDF et CSV de l'historique d'activité
 */
export const useActivityHistoryExport = (
  dataHistoryContainer,
  countDataHistory,
  materials,
  t,
  periodName,
  periodHistoryStart,
  periodHistoryStop,
  keywordFilter,
  resolveHistoryFlux,
) => {
  const { superContainers } = useSuperContainers();
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dataHistoryExport, setDataHistoryExport] = useState([]);
  const [isModalDownload, setIsModalDownload] = useState(false);
  const [disabledDownload, setDisabledDownload] = useState(false);

  const buildAnomalyDeliveryFromOutputSurface = (objectSegmentations) => {
    if (
      !Array.isArray(objectSegmentations) ||
      objectSegmentations.length === 0
    ) {
      return {};
    }

    return objectSegmentations.reduce((acc, segmentation) => {
      const cls = segmentation?.cls;
      if (!cls) return acc;

      if (segmentation?.isAuthorizedMaterial) {
        return acc;
      }

      const hasIsNewObject = Object.hasOwn(segmentation, "isNewObject");
      const shouldCount = segmentation?.isNewObject || !hasIsNewObject;
      if (!shouldCount) return acc;

      const type = String(cls).toLowerCase();
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  };

  const fetchAzureData = async (history) => {
    const blobUrl = buildOutputSurfaceBlobUrl(history);
    if (!blobUrl) {
      history.anomalyDelivery = history.anomalyDelivery || {};
      return history;
    }

    try {
      const wasteObjects = await getAzureBlob({ blobUrl });
      if (wasteObjects.status === 200) {
        const objectSegmentations =
          wasteObjects.data.object_segmentations || [];
        history.wasteObjectsList = objectSegmentations;
        history.anomalyDelivery =
          buildAnomalyDeliveryFromOutputSurface(objectSegmentations);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.error("Resource not found (404)");
      } else {
        console.error("Error during the request :", error.message);
      }
    }
    history.anomalyDelivery = history.anomalyDelivery || {};
    return history;
  };

  const updateProgress = (index, totalLength) => {
    const newProgress = ((index + 1) / totalLength) * 100;
    setProgress(newProgress);
  };

  const exportPDF = async () => {
    setDownloadProgress(true);

    if (countDataHistory > 100) {
      setDisabledDownload(true);
    } else {
      let completedCount = 0;

      const promises = dataHistoryContainer.map((item, index) =>
        fetchAzureData(item).then((result) => {
          completedCount++;
          updateProgress(completedCount, dataHistoryContainer.length);
          return result;
        }),
      );

      const temporalDataArray = await Promise.all(promises);
      setDataHistoryExport(temporalDataArray);
      setIsModalDownload(true);
    }
    setProgress(0);
    setDownloadProgress(false);
  };

  const exportActivityToCSV = async () => {
    setDownloadProgress(true);
    setProgress(0);

    try {
      let resolveFlux = resolveHistoryFlux;
      if (!resolveFlux) {
        const fluxLookup = await buildCollectionFluxLookup({
          periodHistoryStart,
          periodHistoryStop,
          keywordFilter,
          superContainers,
        });
        resolveFlux = fluxLookup.resolveForHistoryItem;
      }

      const materialLabel = materials
        .sort((a, b) => a.referenceName.localeCompare(b.referenceName))
        .map((item) => t(item.referenceName));

      const headers = [
        t("Date"),
        t("Address"),
        t("Container"),
        t("Filling level"),
        t("Stream"),
        t("Rotation"),
        ...materialLabel,
      ];

      const totalRows = dataHistoryContainer.length;
      let completedCount = 0;

      const dataWithAnomalyDelivery = await Promise.all(
        dataHistoryContainer.map(async (item) => {
          let row;
          if (
            item?.anomalyDelivery &&
            Object.keys(item.anomalyDelivery).length > 0
          ) {
            row = item;
          } else if (!item?.image_url) {
            row = { ...item, anomalyDelivery: {} };
          } else {
            row = await fetchAzureData({ ...item });
          }
          completedCount++;
          updateProgress(completedCount, totalRows);
          return row;
        }),
      );

      const csvData = dataWithAnomalyDelivery.map((item) => {
        const formattedAnomalies = Object.entries(item.anomalyDelivery).reduce(
          (acc, [key, value]) => {
            acc[t(key)] = value;
            return acc;
          },
          {},
        );

        const anomaliesData = materialLabel.map((element) => {
          const key = element.replace(/Anomalie |Anomaly /, "");
          return formattedAnomalies[key] !== undefined
            ? formattedAnomalies[key]
            : 0;
        });

        const streamLabel = resolveFlux?.(item) ?? item.wasteName ?? "";

        return [
          moment(item._time).tz("Europe/Paris").format("DD-MM-YYYY HH:mm"),
          `${item.addressName}${item.positioning ? ` | ${item.positioning}` : ""}`,
          formattedTypeContainer(item.type),
          `${item.filling_level}%`,
          streamLabel,
          item.rotation ? t("yes").toUpperCase() : t("no").toUpperCase(),
          ...anomaliesData,
        ];
      });

      const csvContent = [headers, ...csvData]
        .map((row) =>
          row
            .map((cell) => {
              if (
                typeof cell === "string" &&
                (cell.includes(";") || cell.includes("\n"))
              ) {
                return `"${cell}"`;
              }
              return cell;
            })
            .join(";"),
        )
        .join("\n");

      const csvWithBOM = "\uFEFF" + csvContent;

      const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
      const fileName = `${t("Activities")} ${startWithLowercase(periodName)}.csv`;
      await downloadFile(blob, fileName);
    } finally {
      setProgress(0);
      setDownloadProgress(false);
    }
  };

  return {
    downloadProgress,
    progress,
    dataHistoryExport,
    isModalDownload,
    setIsModalDownload,
    disabledDownload,
    setDisabledDownload,
    exportPDF,
    exportActivityToCSV,
  };
};
